/**
 * Maximum number of files uploading to GCS at once, app-wide.
 *
 * Browsers always negotiate HTTP/2 with the GCS host via ALPN and no XHR/fetch API can
 * opt out, so every chunk PUT is a stream on a single TCP connection. Unbounded parallel
 * uploads starve individual streams in Safari/Firefox until GCS kills them with 408
 * (ISSUE-2560); 3-4 files in flight keeps per-stream throughput high and every chunk far
 * from the timeout.
 */
export const UPLOAD_CONCURRENCY = 3

/**
 * Returns a runner that executes tasks with at most `maxConcurrent` in flight, FIFO.
 * Pass a getter to make the bound adjustable while tasks are queued.
 */
export function createUploadPool(maxConcurrent: number | (() => number)) {
  const getMax = typeof maxConcurrent === 'function' ? maxConcurrent : () => maxConcurrent
  let active = 0
  const waiting: Array<() => void> = []

  const acquire = () => new Promise<void>((resolve) => {
    if (active < getMax()) {
      active++
      resolve()
    } else {
      waiting.push(() => {
        active++
        resolve()
      })
    }
  })

  // Re-reads the bound so lowering it mid-drop stops handing out slots; raising it
  // takes effect on the next completion rather than immediately.
  const release = () => {
    active--
    if (active < getMax()) waiting.shift()?.()
  }

  return async <T>(task: () => Promise<T>): Promise<T> => {
    await acquire()
    try {
      return await task()
    } finally {
      release()
    }
  }
}

let concurrency = UPLOAD_CONCURRENCY

/**
 * Overrides the app-wide bound at runtime, so a value that turns out too conservative
 * (or too loose) for a surface can be corrected without a release-production cut.
 * Driven by `NumberFeatureFlag.UPLOAD_CONCURRENCY`; anything unusable keeps the default.
 */
export const setUploadConcurrency = (value: number) => {
  concurrency = Number.isFinite(value) && value >= 1 ? Math.floor(value) : UPLOAD_CONCURRENCY
}

/** The bound in force right now — callers that batch work per pool width read it at call time */
export const getUploadConcurrency = () => concurrency

/** Single shared pool — all GCS uploads ride the same HTTP/2 connection, so the bound must be global */
export const runInUploadPool = createUploadPool(() => concurrency)

/**
 * Maximum number of thumbnail loads in flight, app-wide.
 *
 * A load is one short GET, not a multi-minute chunked PUT, so the bound is wider than
 * UPLOAD_CONCURRENCY — but it rides the same HTTP/2 connection, and an unbounded fan-out
 * (one per visual, ~50 on a full raw assignment since ISSUE-3455 hydrates the CT upload
 * page at mount) starves the upload streams the CT actually cares about.
 */
export const LOAD_CONCURRENCY = 6

/** Single shared pool — see runInUploadPool, the bound must be global for the same reason */
export const runInLoadPool = createUploadPool(LOAD_CONCURRENCY)
